import { Key, useMemo } from 'react';
import { Table as ATable, TableColumnsType } from 'antd';
import { StyledTableHeaderCell, StyledTableWrapper } from 'components/new-ui/base/table/styles';
import { ListPayload } from 'core/http/response';
import getProductsTablePagination from 'views/declarations/common/getProductsTablePagination';
import { ItemFieldTemplateRow } from 'store/item-field-templates/itemFieldTemplate';
import { Link } from 'react-router-dom';

type Props = {
    data: ListPayload<ItemFieldTemplateRow>;
    getTemplatePath: (id: string) => string | undefined;
    loading?: boolean;
    selectedRowKey?: Key;
    onSelectedRowKeyChange: (key: Key) => void;
    onPageChange: (pageNumber: number, pageSize: number) => void;
};

const getColumns = (getTemplatePath: Props['getTemplatePath']): TableColumnsType<ItemFieldTemplateRow> => [
    {
        title: 'Template Name',
        key: 'TEMPLATE_NAME',
        render: (_, record) => record.name,
    },
    {
        title: 'Fields Included',
        key: 'FIELDS',
        render: (_, record) => record.fieldsIncluded.join(', '),
    },
    {
        title: 'Codes',
        key: 'CODES',
        render: (_, record) => record.codes.join(', '),
    },
    {
        title: 'Actions',
        key: 'ACTIONS',
        render: (_, record) => {
            const to = getTemplatePath(record.id);
            if (!to) return null;

            return (
                <Link target="_blank" rel="opener" to={to}>
                    View
                </Link>
            );
        },
    },
];

const Table = ({ data, getTemplatePath, loading, selectedRowKey, onSelectedRowKeyChange, onPageChange }: Props) => {
    const columns = useMemo(() => getColumns(getTemplatePath), [getTemplatePath]);

    return (
        <StyledTableWrapper>
            <ATable<ItemFieldTemplateRow>
                rowKey="id"
                loading={loading}
                dataSource={data.list}
                columns={columns}
                pagination={getProductsTablePagination(data)}
                // One template can be applied at a time for this phase
                rowSelection={{
                    type: 'radio',
                    selectedRowKeys: selectedRowKey === undefined ? [] : [selectedRowKey],
                    onChange: ([key]) => onSelectedRowKeyChange(key),
                }}
                onChange={({ current = 1, pageSize = data.pageSize }) => onPageChange(current - 1, pageSize)}
                components={{
                    header: {
                        cell: (props: any) => <StyledTableHeaderCell {...props} />,
                    },
                }}
            />
        </StyledTableWrapper>
    );
};

export default Table;
